import { showDialog } from '@/utils/dialogs';
import React, { useState } from 'react';
import { View, ScrollView, TouchableOpacity, ActivityIndicator, TextInput } from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { MaterialIcons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';

// React Native Reusables Components
import { Card, CardContent } from '@/components/ui/card';
import { Text } from '@/components/ui/text';

// Services & Utils
import { apiRequest } from '../../services/apiClient';
import { ROUTES } from '../../navigation/routes';
import { useTranslation } from '../../hooks/useTranslation';
import { useResponsive } from '../../hooks/useResponsive';
import { brand } from '../../design-system/theme/tokens';
import {
  SCHOOL_CURRENCIES,
  apiErrorText,
  formatCents,
  parseMajorToCents,
  parseWholeNumber,
  validateCreateSchoolForm,
  type CreateSchoolErrors,
  type CreateSchoolField,
  type SchoolCurrency,
} from './createSchoolForm';

// Professional Design Tokens
const COLORS = {
  primary: brand.primary,
  secondary: '#F59E0B',
  success: '#10B981',
  error: '#DC2626',
  background: '#FFFFFF',
  surface: '#F9FAFB',
};

const INPUT_CLASS = 'bg-gray-50 border rounded-lg px-4 py-3 text-base text-foreground';

export default function CreateSchoolScreenEnhanced() {
  const router = useRouter();
  const { t } = useTranslation();
  const insets = useSafeAreaInsets();
  const { isPhone } = useResponsive();

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [maxStudents, setMaxStudents] = useState('50');
  const [harshness, setHarshness] = useState('5');
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<CreateSchoolErrors>({});

  const [billingCurrency, setBillingCurrency] = useState<SchoolCurrency>('USD');
  // Major units (e.g. 299.00); converted to cents only when building the payload.
  const [basePrice, setBasePrice] = useState('299.00');
  const [baseSeats, setBaseSeats] = useState('10');
  const [addonPrice, setAddonPrice] = useState('149.00');
  const [addonPackageSize, setAddonPackageSize] = useState('5');
  // Language of emails to members without their own language setting (D22).
  // null = derive from the currency on the server (TRY -> Turkish, else English).
  const [defaultLanguage, setDefaultLanguage] = useState<'en' | 'tr' | null>(null);

  const rowClass = isPhone ? 'flex-col gap-4' : 'flex-row gap-3';
  const cellClass = isPhone ? 'gap-2' : 'flex-1 gap-2';

  const basePriceCents = parseMajorToCents(basePrice);
  const addonPriceCents = parseMajorToCents(addonPrice);
  const baseSeatsValue = parseWholeNumber(baseSeats);
  const addonSeatsValue = parseWholeNumber(addonPackageSize);
  const priceSummary =
    basePriceCents !== null && addonPriceCents !== null && baseSeatsValue !== null && addonSeatsValue !== null
      ? t('createSchool.priceSummary', {
          basePrice: formatCents(basePriceCents, billingCurrency),
          baseSeats: baseSeatsValue,
          addonPrice: formatCents(addonPriceCents, billingCurrency),
          addonSeats: addonSeatsValue,
        })
      : null;

  const clearError = (field: CreateSchoolField) => {
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const renderField = (
    field: CreateSchoolField,
    label: string,
    value: string,
    onChange: (text: string) => void,
    opts: { keyboardType?: 'default' | 'number-pad' | 'decimal-pad'; placeholder?: string; help?: string; required?: boolean; autoCapitalize?: 'none' } = {},
  ) => (
    <View className={cellClass}>
      <Text className="text-base font-semibold text-foreground">
        {label}
        {opts.required ? <Text className="text-red-600" accessibilityLabel={t('createSchool.required')}> *</Text> : null}
      </Text>
      <TextInput
        testID={`create-school-${field}`}
        value={value}
        onChangeText={(text) => {
          onChange(text);
          clearError(field);
        }}
        keyboardType={opts.keyboardType}
        placeholder={opts.placeholder}
        autoCapitalize={opts.autoCapitalize}
        className={`${INPUT_CLASS} ${errors[field] ? 'border-red-500' : 'border-gray-200'}`}
        placeholderTextColor="#9ca3af"
        accessibilityLabel={label}
      />
      {errors[field] ? (
        <Text testID={`create-school-${field}-error`} className="text-sm text-red-600">
          {t(errors[field]!)}
        </Text>
      ) : opts.help ? (
        <Text className="text-sm text-muted-foreground">{opts.help}</Text>
      ) : null}
    </View>
  );

  const onSubmit = async () => {
    const result = validateCreateSchoolForm({
      name,
      slug,
      maxStudents,
      harshness,
      currency: billingCurrency,
      basePrice,
      baseSeats,
      addonPrice,
      addonPackageSize,
      defaultLanguage,
    });
    if ('errors' in result) {
      setErrors(result.errors);
      showDialog(t('createSchool.fixErrors'));
      return;
    }
    setErrors({});

    setSaving(true);
    let createdSchool: { id: number; name: string };
    try {
      createdSchool = await apiRequest<{ id: number; name: string }>({
        endpoint: '/schools/',
        method: 'POST',
        data: result.school,
      });
    } catch (e: unknown) {
      showDialog(apiErrorText(e, t('common.error')));
      setSaving(false);
      return;
    }

    // ADM-4: the create endpoint does not take billing fields yet, so the plan
    // is a second call. If it fails the school already exists; say so plainly
    // instead of a generic error that invites a retry ("slug already exists").
    try {
      await apiRequest({
        endpoint: `/schools/${createdSchool.id}/billing-plan`,
        method: 'PUT',
        data: result.billing,
      });
    } catch (e: unknown) {
      showDialog(
        t('createSchool.billingSaveFailedTitle'),
        t('createSchool.billingSaveFailedMessage', {
          name: createdSchool.name,
          id: createdSchool.id,
          error: apiErrorText(e, t('common.error')),
        }),
      );
      setSaving(false);
      router.replace(ROUTES.TABS_PLATFORM_ADMIN);
      return;
    }

    setSaving(false);
    showDialog(t('createSchool.successMessage'), `${t('createSchool.name')}: ${createdSchool.name}`);
    router.replace(ROUTES.TABS_PLATFORM_ADMIN);
  };

  const languageLabel = (code: 'en' | 'tr' | null) =>
    code === null
      ? t('createSchool.emailLanguageAuto')
      : code === 'en'
        ? t('createSchool.emailLanguageEnglish')
        : t('createSchool.emailLanguageTurkish');

  return (
    <SafeAreaView className="flex-1 bg-surface">
      {/* Header */}
      <View className="bg-white px-4 md:px-6 py-3 border-b border-gray-200">
        <View className="flex-row items-center">
          <TouchableOpacity
            testID="create-school-back"
            onPress={() => router.replace(ROUTES.TABS_PLATFORM_ADMIN)}
            className="mr-2 items-center justify-center rounded-full"
            style={{ width: 44, height: 44 }}
            hitSlop={{ top: 4, bottom: 4, left: 4, right: 4 }}
            accessibilityRole="button"
            accessibilityLabel={t('common.back')}
          >
            <MaterialIcons name="arrow-back" size={24} color={COLORS.primary} />
          </TouchableOpacity>
          <View className="flex-1">
            <Text className="text-2xl font-bold text-foreground">{t('createSchool.title')}</Text>
            <Text className="text-sm text-muted-foreground mt-1">{t('createSchool.subtitle')}</Text>
          </View>
        </View>
      </View>

      <ScrollView
        className="flex-1"
        contentContainerStyle={{
          paddingBottom: Math.max(insets.bottom + 16, 32),
        }}
        showsVerticalScrollIndicator={false}
      >
        <View className="w-full max-w-2xl mx-auto px-4 md:px-6 lg:px-8 py-8">
        <Card>
          <CardContent className={`${isPhone ? 'p-4' : 'p-6'} gap-4`}>
            {renderField('name', t('createSchool.schoolName'), name, setName, {
              placeholder: t('createSchool.schoolNamePlaceholder'),
              required: true,
            })}

            {renderField('slug', t('createSchool.slug'), slug, setSlug, {
              placeholder: t('createSchool.slugPlaceholder'),
              help: t('createSchool.slugHelp'),
              autoCapitalize: 'none',
              required: true,
            })}

            <View className={rowClass}>
              {renderField('maxStudents', t('createSchool.maxStudents'), maxStudents, setMaxStudents, {
                keyboardType: 'number-pad',
                help: t('createSchool.maxStudentsHelp'),
                required: true,
              })}
              {renderField('harshness', t('createSchool.harshness'), harshness, setHarshness, {
                keyboardType: 'number-pad',
                help: t('createSchool.harshnessHelp'),
                required: true,
              })}
            </View>

            <View className="border-t border-gray-200 pt-4 mt-4 gap-4">
              <View className="gap-1">
                <Text className="text-lg font-bold text-foreground">{t('createSchool.billingTitle')}</Text>
                <Text className="text-sm text-muted-foreground">{t('createSchool.billingSubtitle')}</Text>
              </View>

              <View className="gap-2">
                <Text className="text-base font-semibold text-foreground">{t('createSchool.currency')}</Text>
                <View className="flex-row gap-3" accessibilityRole="radiogroup">
                  {SCHOOL_CURRENCIES.map((code) => (
                    <TouchableOpacity
                      key={code}
                      testID={`create-school-currency-${code}`}
                      accessibilityRole="radio"
                      accessibilityState={{ selected: billingCurrency === code }}
                      onPress={() => setBillingCurrency(code)}
                      className={`px-4 py-2 rounded-lg border ${billingCurrency === code ? 'bg-primary border-primary' : 'bg-gray-50 border-gray-200'}`}
                      style={{ minHeight: 44, justifyContent: 'center' }}
                    >
                      <Text className={billingCurrency === code ? 'text-primary-foreground font-semibold' : 'text-foreground'}>
                        {code}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>

              <View className={rowClass}>
                {renderField('basePrice', t('createSchool.basePrice'), basePrice, setBasePrice, {
                  keyboardType: 'decimal-pad',
                  placeholder: '49.00',
                  required: true,
                })}
                {renderField('baseSeats', t('createSchool.baseSeats'), baseSeats, setBaseSeats, {
                  keyboardType: 'number-pad',
                  required: true,
                })}
              </View>
              <View className={rowClass}>
                {renderField('addonPrice', t('createSchool.addonPrice'), addonPrice, setAddonPrice, {
                  keyboardType: 'decimal-pad',
                  placeholder: '49.00',
                  required: true,
                })}
                {renderField('addonPackageSize', t('createSchool.addonPackageSize'), addonPackageSize, setAddonPackageSize, {
                  keyboardType: 'number-pad',
                  required: true,
                })}
              </View>

              {priceSummary ? (
                <Text testID="create-school-price-summary" className="text-sm text-foreground bg-gray-50 rounded-lg px-3 py-2">
                  {priceSummary}
                </Text>
              ) : null}

              <View className="gap-2">
                <Text className="text-base font-semibold text-foreground">{t('createSchool.emailLanguage')}</Text>
                <Text className="text-sm text-muted-foreground">{t('createSchool.emailLanguageHelp')}</Text>
                <View className="flex-row flex-wrap gap-3">
                  {([null, 'en', 'tr'] as const).map((code) => (
                    <TouchableOpacity
                      key={code ?? 'auto'}
                      testID={`school-default-language-${code ?? 'auto'}`}
                      accessibilityRole="radio"
                      accessibilityState={{ selected: defaultLanguage === code }}
                      onPress={() => setDefaultLanguage(code)}
                      className={`px-4 py-2 rounded-lg border ${defaultLanguage === code ? 'bg-primary border-primary' : 'bg-gray-50 border-gray-200'}`}
                      style={{ minHeight: 44, justifyContent: 'center' }}
                    >
                      <Text className={defaultLanguage === code ? 'text-primary-foreground font-semibold' : 'text-foreground'}>
                        {languageLabel(code)}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
            </View>

            <TouchableOpacity
              testID="create-school-submit"
              disabled={saving}
              onPress={onSubmit}
              className="mt-4 flex-row items-center justify-center gap-2 rounded-lg py-4"
              style={{
                backgroundColor: saving ? '#93C5FD' : COLORS.primary,
              }}
              accessibilityRole="button"
              accessibilityLabel={t('createSchool.createButton')}
            >
              {saving ? (
                <ActivityIndicator color="white" />
              ) : (
                <MaterialIcons name="save" size={20} color="white" />
              )}
              <Text className="text-base font-bold text-white">
                {saving ? t('createSchool.creating') : t('createSchool.createButton')}
              </Text>
            </TouchableOpacity>
          </CardContent>
        </Card>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}
